import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { FileSpreadsheet } from 'lucide-react';
import { api } from '@/shared/api/axios';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Spinner } from '@/shared/components/ui/Spinner';
import { cn, localToday } from '@/shared/utils/utils';

const BASE = '/api/accounts/sales-invoices/export';

function thisMonth() {
  return localToday().slice(0, 7);
}

const STATUS_OPTIONS = [
  { value: '', label: 'All (with cancelled)' },
  { value: 'active', label: 'Active only (no cancelled)' },
  { value: 'paid', label: 'Paid' },
  { value: 'partially_paid', label: 'Partly paid' },
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'cancelled', label: 'Cancelled only' },
];

/**
 * Sales invoices to Excel — by month or any date range, for everyone or one
 * sales employee. For the Accounts team, Admin and Super Admin.
 */
export function InvoiceExportDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [mode, setMode] = useState<'month' | 'range'>('month');
  const [month, setMonth] = useState(thisMonth());
  const [from, setFrom] = useState(`${thisMonth()}-01`);
  const [to, setTo] = useState(localToday());
  const [employee, setEmployee] = useState('');
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);

  const { data: employees = [], isLoading } = useQuery({
    queryKey: ['invoice-export', 'employees'],
    queryFn: async () => (await api.get<{ employees: { id: string; name: string }[] }>(`${BASE}/options`)).data.employees,
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  const download = async () => {
    if (mode === 'range' && (!from || !to || from > to)) { toast.error('Pick a from date on or before the to date.'); return; }
    const params = new URLSearchParams();
    if (mode === 'month') params.set('month', month);
    else { params.set('from', from); params.set('to', to); }
    if (employee) params.set('employee', employee);
    if (status) params.set('status', status);
    setBusy(true);
    try {
      const res = await api.get(`${BASE}/xlsx?${params}`, { responseType: 'blob' });
      const disposition = String(res.headers['content-disposition'] || '');
      const name = /filename="([^"]+)"/.exec(disposition)?.[1] || 'Sales_Invoices.xlsx';
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
      toast.success('Excel downloaded.');
      onClose();
    } catch (err) {
      // The error body arrives as a Blob because of responseType: 'blob'.
      let msg = 'Could not prepare the Excel file.';
      const data = (err as { response?: { data?: Blob } })?.response?.data;
      if (data instanceof Blob) { try { msg = JSON.parse(await data.text()).error || msg; } catch { /* keep default */ } }
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const field = 'w-full rounded-lg border border-outline bg-surface px-3 py-2 text-body-md outline-none focus:border-primary';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Export invoices to Excel"
      footer={(
        <div className="flex w-full justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={download} disabled={busy}>
            {busy ? <Spinner className="mr-2 h-4 w-4" /> : <FileSpreadsheet className="mr-2 h-4 w-4" />} Download Excel
          </Button>
        </div>
      )}
    >
      <div className="flex flex-col gap-4">
        <div className="inline-flex self-start rounded-lg bg-surface-variant p-1">
          {([['month', 'Month'], ['range', 'Custom dates']] as const).map(([v, label]) => (
            <button
              key={v}
              type="button"
              onClick={() => setMode(v)}
              className={cn('rounded-md px-3 py-1.5 text-body-sm', mode === v ? 'bg-surface font-semibold text-on-surface shadow-sm' : 'text-on-surface-variant')}
            >
              {label}
            </button>
          ))}
        </div>

        {mode === 'month' ? (
          <label className="flex flex-col gap-1">
            <span className="text-label-md text-on-surface-variant">Month</span>
            <input type="month" value={month} max={thisMonth()} onChange={(e) => setMonth(e.target.value)} className={field} />
          </label>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1">
              <span className="text-label-md text-on-surface-variant">From</span>
              <input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} className={field} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-label-md text-on-surface-variant">To</span>
              <input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} className={field} />
            </label>
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1">
            <span className="text-label-md text-on-surface-variant">Sales employee</span>
            <select value={employee} onChange={(e) => setEmployee(e.target.value)} className={field} disabled={isLoading}>
              <option value="">All employees</option>
              {employees.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-label-md text-on-surface-variant">Status</span>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className={field}>
              {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </label>
        </div>

        <p className="text-body-sm text-on-surface-variant">
          Three sheets: <strong>Invoices</strong> (number, date, customer, GSTIN, city, state, pincode, delivery location, sales employee,
          taxable value, GST, total, received, balance due), <strong>Items</strong> (every product line) and <strong>By Employee</strong> (totals).
          Cancelled invoices are listed with the date and reason, but left out of every total. Draft invoices are not included.
        </p>
      </div>
    </Modal>
  );
}
